import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, createRef, useRef } from 'react';
import FormContainer from "../../../components/FormContainer";
import Menu from '../../../models/Menu';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { PageContainerActionParamType } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import { validate as uuidValidate } from "uuid";
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';

const groupName: MenuGroupName = "Umum";
const subgroupName: string = "Pengaturan";
const code: MenuCode = "GLOBAL-MENU";
const title: MenuTitle = "Menu";
const endpoint = "menu";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<Menu>({ kode: '', nama: '' });
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
	const navigate = useNavigate();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => setFormData({...response.data?.data}))
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: Menu | undefined) => {
        if (!data?.nama)
            return false;

        if (!data?.kode)
            return false;

        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Menu> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

		const dataToSend = {
            ucode: param.data.ucode,
            kode: param.data.kode,
            nama: param.data.nama,
			aktif: param.data.aktif,
		}

		const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(dataToSend)) : apiCaller.post(endpoint, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
		<>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<Menu>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
                    isViewMode={isViewMode}
                    fields={[
                        {
                            name: "kode",
                            title: "Kode Menu",
                            inputType: "text",
                            dataType: (typeof formData.kode),
                            isReadonly: false,
                            isRequired: formData?.ucode ? true : false,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Menu>) => onChanged(valueObj),
                        },
                        {
                            name: "nama",
                            title: "Nama Menu",
                            inputType: "text",
                            dataType: (typeof formData.nama),
                            isReadonly: false,
                            isRequired: true,
                            onChanged: (valueObj: DataFormFieldChangeParamType<Menu>) => onChanged(valueObj),
                        }
                    ]}
                    onValidate={onValidate}
                    onClose={(pathObject) => onClose(pathObject)}
                    onSave={(data) => onSave(data)}
                />
            </FormContainer>
		</>
    );
};

export default Form;
